import type { FocusEvent } from 'react';
import { useRef, useState } from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { selectParticipationDocData } from 'features/participations/store/participationSelectors';
import { useRouteMatch } from 'features/routing/useRouteMatch';
import { useAppSelector } from 'features/store/hooks';
import { BRAND_MARK_SIZE, BrandLogo } from './BrandLogo';
import { NAV_ITEMS, NAV_PATTERNS } from './navItems';
import { NavProfile } from './NavProfile';
import { SHELL_GAP, SHELL_RADIUS } from './shellGeometry';
import type { NavItemConfig } from './navItems';
import type { Theme } from '@mui/material/styles';

/** Collapsed rail geometry: card width, horizontal padding and 1px border
 *  leave a 50px pill; the inset centers the icon in it and stays fixed when
 *  the rail expands so the icon never moves. */
const RAIL_WIDTH = 76;
const RAIL_PADDING_X = 12;
const RAIL_BORDER = 1;
const NAV_ICON_SIZE = 21;
const RAIL_PILL_WIDTH = RAIL_WIDTH - 2 * (RAIL_PADDING_X + RAIL_BORDER);
const RAIL_ICON_INSET = (RAIL_PILL_WIDTH - NAV_ICON_SIZE) / 2;
const RAIL_BRAND_INSET = (RAIL_PILL_WIDTH - BRAND_MARK_SIZE) / 2;

/** Shared dock/rail card look; each branch adds its own layout properties. */
const cardSx = (theme: Theme) => ({
  backgroundColor: 'background.paper',
  border: `1px solid ${theme.ui.surfaceBorder}`,
  borderRadius: `${String(SHELL_RADIUS)}px`,
});

const NavItem = ({
  item,
  active,
  labelVisible,
  rail,
  participationId,
}: {
  item: NavItemConfig;
  active: boolean;
  labelVisible: boolean;
  rail: boolean;
  participationId: string;
}) => {
  const { t } = useTranslation();
  return (
    <ButtonBase
      component={Link}
      to={item.route(participationId)}
      aria-current={active ? 'page' : undefined}
      sx={theme => ({
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-start',
        gap: labelVisible ? 1.75 : 0,
        minHeight: 44,
        pl: rail ? `${String(RAIL_ICON_INSET)}px` : 1.5,
        pr: 1.5,
        borderRadius: '10px',
        fontSize: 14.5,
        fontWeight: active ? 700 : 500,
        color: active ? 'primary.contrastText' : 'text.primary',
        backgroundColor: active ? 'primary.main' : 'transparent',
        '&:hover': {
          backgroundColor: active
            ? 'primary.main'
            : theme.palette.primary.selected,
        },
        '&.Mui-focusVisible': {
          outline: `2px solid ${theme.palette.primary.main}`,
          outlineOffset: 2,
        },
      })}
    >
      <item.Icon
        sx={{
          fontSize: NAV_ICON_SIZE,
          flexShrink: 0,
          color: active ? 'primary.contrastText' : 'text.secondary',
        }}
      />
      <Typography
        sx={{
          fontSize: 'inherit',
          fontWeight: 'inherit',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          width: labelVisible ? 'auto' : 0,
          opacity: labelVisible ? 1 : 0,
          transition: 'opacity 150ms',
        }}
      >
        {t(item.labelKey)}
      </Typography>
    </ButtonBase>
  );
};

type SideNavProps = {
  /** Collapsed 76px icon rail that expands on hover/focus as an overlay
   *  (narrow-desktop tier). */
  rail?: boolean;
};

/** Desktop floating dock: brand, pill nav, profile footer. In `rail` mode it
 *  collapses to icons and hover/focus-expands over the content. */
export const SideNav = ({ rail = false }: SideNavProps) => {
  const { t } = useTranslation();
  const routeMatch = useRouteMatch(NAV_PATTERNS);
  const currentPattern = routeMatch?.pattern.path;
  const participationId = useAppSelector(selectParticipationDocData).id;

  const [isHovering, setIsHovering] = useState(false);
  const [isFocusWithin, setIsFocusWithin] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const expanded = !rail || isHovering || isFocusWithin || menuOpen;

  // Focus gained from a pointer press (a click on a link or the profile
  // button) must not hold the rail open once the pointer leaves; only
  // keyboard focus does. The press arms the flag and the focus it causes
  // consumes it, so a release outside the rail or a cancelled touch cannot
  // leave it armed.
  const pointerPressRef = useRef(false);
  const handleFocus = () => {
    if (pointerPressRef.current) {
      pointerPressRef.current = false;
      return;
    }
    setIsFocusWithin(true);
  };
  const handleBlur = (event: FocusEvent<HTMLElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget)) {
      setIsFocusWithin(false);
    }
  };

  const content = (
    <>
      <Box
        data-testid="brand-slot"
        sx={{
          pt: '2px',
          pb: '18px',
          pr: '6px',
          pl: rail ? `${String(RAIL_BRAND_INSET)}px` : '6px',
        }}
      >
        <BrandLogo labelVisible={expanded} />
      </Box>
      <Box
        component="nav"
        aria-label={t('ShellNavLabel')}
        sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, flex: 1 }}
      >
        {NAV_ITEMS.map(item => (
          <NavItem
            key={item.id}
            item={item}
            rail={rail}
            participationId={participationId}
            active={item.route() === currentPattern}
            labelVisible={expanded}
          />
        ))}
      </Box>
      <Box
        sx={{
          pt: 1,
          mt: 1,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <NavProfile expanded={expanded} onMenuOpenChange={setMenuOpen} />
      </Box>
    </>
  );

  // One root shape across both tiers, so crossing the xl breakpoint keeps the
  // card mounted: the in-flow spacer holds slot 0 (rail only) and the card
  // holds slot 1.
  return (
    <>
      {rail && <Box sx={{ width: RAIL_WIDTH, flexShrink: 0 }} />}
      <Box
        component="aside"
        data-expanded={rail ? expanded : undefined}
        onMouseEnter={rail ? () => setIsHovering(true) : undefined}
        onMouseLeave={rail ? () => setIsHovering(false) : undefined}
        onPointerDownCapture={
          rail
            ? () => {
                pointerPressRef.current = true;
              }
            : undefined
        }
        onPointerUpCapture={
          rail
            ? () => {
                pointerPressRef.current = false;
              }
            : undefined
        }
        onPointerCancelCapture={
          rail
            ? () => {
                pointerPressRef.current = false;
              }
            : undefined
        }
        onFocusCapture={rail ? handleFocus : undefined}
        onBlurCapture={rail ? handleBlur : undefined}
        sx={theme => ({
          ...cardSx(theme),
          display: 'flex',
          flexDirection: 'column',
          ...(rail
            ? {
                position: 'absolute',
                left: SHELL_GAP,
                top: SHELL_GAP,
                bottom: SHELL_GAP,
                width: expanded ? 248 : RAIL_WIDTH,
                boxShadow: expanded ? theme.ui.shadowLift : theme.ui.shadow,
                transition: 'width 220ms cubic-bezier(0.4, 0, 0.2, 1)',
                overflow: 'hidden',
                zIndex: theme.zIndex.appBar,
                p: `16px ${String(RAIL_PADDING_X)}px`,
              }
            : {
                width: 236,
                flexShrink: 0,
                boxShadow: theme.ui.shadow,
                p: '20px 14px',
              }),
        })}
      >
        {content}
      </Box>
    </>
  );
};
